import {type DidString} from '@atproto/syntax'

import packageJson from '#/../package.json'
import {BRAND} from '#/config/brand'
import {OAUTH_SIGNUP_PDS_HOST} from '#/config/oauth'

/**
 * The semver version of the app, as defined in `package.json.`
 *
 * N.B. The fallback is needed for Render.com deployments
 */
export const RELEASE_VERSION: string =
  process.env.EXPO_PUBLIC_RELEASE_VERSION || packageJson.version

/**
 * The env the app is running in e.g. development, testflight, production, e2e
 */
export const ENV: string = process.env.EXPO_PUBLIC_ENV as
  'production' | 'testflight' | 'development' | 'e2e' | (string & {})

/**
 * Indicates whether the app is running in TestFlight
 */
export const IS_TESTFLIGHT = ENV === 'testflight'

/**
 * Indicates whether the app is `__DEV__`
 */
export const IS_DEV = __DEV__

/**
 * Indicates whether the app is running in a test environment
 */
export const IS_E2E = ENV === 'e2e'

/**
 * Indicates whether the app is `__DEV__` or TestFlight
 */
export const IS_INTERNAL = IS_DEV || IS_TESTFLIGHT

/**
 * The commit hash that the current bundle was made from. The user can
 * see the commit hash in the app's settings along with the other version info.
 * Useful for debugging/reporting.
 */
export const BUNDLE_IDENTIFIER: string =
  process.env.EXPO_PUBLIC_BUNDLE_IDENTIFIER || 'dev'

/**
 * This will always be in the format of YYMMDDHH, so that it always increases
 * for each build. This should only be used for analytics reporting and shouldn't
 * be used to identify a specific bundle.
 */
export const BUNDLE_DATE: number =
  process.env.EXPO_PUBLIC_BUNDLE_DATE === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_BUNDLE_DATE)

/**
 * The log level for the app.
 */
export const LOG_LEVEL = (process.env.EXPO_PUBLIC_LOG_LEVEL || 'info') as
  'debug' | 'info' | 'warn' | 'error'

/**
 * Enable debug logs for specific logger instances
 */
export const LOG_DEBUG: string = process.env.EXPO_PUBLIC_LOG_DEBUG || ''

/**
 * The DID of the appview to proxy authed requests to. Defaults to the brand's
 * configured appview DID (see src/config/brand.ts -> services.appViewDid) so
 * switching the appview is a single edit there; override per-deploy with
 * EXPO_PUBLIC_BLUESKY_PROXY_DID if it must differ from the brand default.
 */
export const BLUESKY_PROXY_DID: DidString =
  process.env.EXPO_PUBLIC_BLUESKY_PROXY_DID || BRAND.services.appViewDid

/**
 * Percentage (0-100) of eligible read-only Bluesky AppView calls also sent to
 * the candidate AppView. OAuth/DPoP calls are excluded because their proofs
 * cannot be safely replayed. Invalid values disable shadowing; out-of-range
 * values are clamped so rollout configuration cannot amplify traffic above
 * 100%.
 */
const requestedAppViewShadowPercentage = Number(
  process.env.EXPO_PUBLIC_APPVIEW_SHADOW_PERCENTAGE,
)
export const APPVIEW_SHADOW_PERCENTAGE: number = Number.isFinite(
  requestedAppViewShadowPercentage,
)
  ? Math.min(100, Math.max(0, requestedAppViewShadowPercentage))
  : 0

/** Candidate AppView used for sampled shadow reads. */
export const APPVIEW_SHADOW_URL: string =
  process.env.EXPO_PUBLIC_APPVIEW_SHADOW_URL || 'https://api.eurosky.network'
export const APPVIEW_SHADOW_DID: string =
  process.env.EXPO_PUBLIC_APPVIEW_SHADOW_DID || 'did:web:api.eurosky.network'

/**
 * The DID of the chat service to proxy to
 */
export const CHAT_PROXY_DID: DidString =
  process.env.EXPO_PUBLIC_CHAT_PROXY_DID || 'did:web:api.bsky.chat'

/**
 * Service whose `/xrpc/_health` decides whether the app considers itself
 * online (see lib/react-query.tsx). This gates TanStack Query's global
 * `onlineManager`, so it must point at a service we operate.
 *
 * It previously used the Bluesky public AppView. That made an AppView outage
 * fatal to the whole app: one failed request flipped `onlineManager` offline,
 * which pauses every query - including authed ones bound for our own AppView -
 * and recovery was gated on a probe against the very service that was down.
 * Defaults to the signup PDS because it is first-party and always reachable
 * when the network is. Override per-deploy with EXPO_PUBLIC_HEALTH_PROBE_SERVICE.
 */
export const HEALTH_PROBE_SERVICE: string =
  process.env.EXPO_PUBLIC_HEALTH_PROBE_SERVICE || OAUTH_SIGNUP_PDS_HOST

/**
 * Metrics API host
 */
export const METRICS_API_HOST: string =
  process.env.EXPO_PUBLIC_METRICS_API_HOST || 'https://events.bsky.app'

/**
 * Growthbook API host
 */
export const GROWTHBOOK_API_HOST: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_API_HOST || `${METRICS_API_HOST}/gb`

/**
 * Growthbook client key
 */
export const GROWTHBOOK_CLIENT_KEY: string =
  process.env.EXPO_PUBLIC_GROWTHBOOK_CLIENT_KEY || 'sdk-7gkUkGy9wguUjyFe'

/**
 * Plausible Analytics site domain, as configured in Plausible's settings. When
 * unset, the Plausible analytics sink is disabled entirely. Plausible is a
 * secondary, privacy-first sink that receives an allowlisted subset of events;
 * the primary metrics pipeline is unaffected by this value.
 */
export const PLAUSIBLE_DOMAIN: string | undefined =
  process.env.EXPO_PUBLIC_PLAUSIBLE_DOMAIN

/**
 * Plausible API host. Points at Plausible Cloud by default, but can be set to a
 * self-hosted instance or a first-party proxy. The tracker posts to
 * `${PLAUSIBLE_API_HOST}/api/event`.
 */
export const PLAUSIBLE_API_HOST: string =
  process.env.EXPO_PUBLIC_PLAUSIBLE_API_HOST || 'https://plausible.io'

/**
 * When `true`, the Plausible sink prints each event it would send to the
 * console and skips the network entirely. Useful for local development: no
 * domain or dashboard required, and works on both web and native. Defaults to
 * off.
 */
export const PLAUSIBLE_DEBUG: boolean =
  process.env.EXPO_PUBLIC_PLAUSIBLE_DEBUG === 'true'

/**
 * Sentry DSN for telemetry
 */
export const SENTRY_DSN: string | undefined = process.env.EXPO_PUBLIC_SENTRY_DSN

/**
 * Bitdrift API key. If undefined, Bitdrift should be disabled.
 */
export const BITDRIFT_API_KEY: string | undefined =
  process.env.EXPO_PUBLIC_BITDRIFT_API_KEY

/**
 * GCP project ID which is required for native device attestation. On web, this
 * should be unset and evaluate to 0.
 */
export const GCP_PROJECT_ID: number =
  process.env.EXPO_PUBLIC_GCP_PROJECT_ID === undefined
    ? 0
    : Number(process.env.EXPO_PUBLIC_GCP_PROJECT_ID)

/**
 * URLs for the app config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const GEOLOCATION_DEV_URL = process.env.GEOLOCATION_DEV_URL
/**
 * Eurosky: overridable so web builds can use our first-party Bunny endpoint
 * (see services/geolocation/). Bluesky's ip.bsky.app is CORS-locked to bsky.app.
 */
export const GEOLOCATION_PROD_URL =
  process.env.EXPO_PUBLIC_GEOLOCATION_URL || `https://ip.bsky.app`
export const GEOLOCATION_URL = IS_DEV
  ? (GEOLOCATION_DEV_URL ?? GEOLOCATION_PROD_URL)
  : GEOLOCATION_PROD_URL

/**
 * URLs for the live-event config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const LIVE_EVENTS_DEV_URL = process.env.LIVE_EVENTS_DEV_URL
export const LIVE_EVENTS_PROD_URL = `https://live-events.workers.bsky.app`
export const LIVE_EVENTS_URL = IS_DEV
  ? (LIVE_EVENTS_DEV_URL ?? LIVE_EVENTS_PROD_URL)
  : LIVE_EVENTS_PROD_URL

/**
 * URLs for the app-config web worker. Can be a
 * locally running server, see `env.example` for more.
 */
export const APP_CONFIG_DEV_URL = process.env.APP_CONFIG_DEV_URL
export const APP_CONFIG_PROD_URL = `https://app-config.workers.bsky.app`
export const APP_CONFIG_URL = IS_DEV
  ? (APP_CONFIG_DEV_URL ?? APP_CONFIG_PROD_URL)
  : APP_CONFIG_PROD_URL

/**
 * Service enable flags. Default true. Set to 'false' to disable.
 */
export const ENABLE_GEOLOCATION =
  process.env.EXPO_PUBLIC_ENABLE_GEOLOCATION !== 'false'
export const ENABLE_LIVE_EVENTS =
  process.env.EXPO_PUBLIC_ENABLE_LIVE_EVENTS !== 'false'
export const ENABLE_APP_CONFIG =
  process.env.EXPO_PUBLIC_ENABLE_APP_CONFIG !== 'false'
/**
 * Gates the web product-analytics metrics client (the `ax.metric()` pipeline
 * that POSTs to `${METRICS_API_HOST}/t`). Native product analytics are always
 * disabled, regardless of this value. Disabling this client also stops its
 * GrowthBook `experiment:viewed`/`feature:viewed` exposures. GrowthBook flag
 * fetching, Plausible, and feed feedback are independent and unaffected.
 */
export const ENABLE_METRICS = process.env.EXPO_PUBLIC_ENABLE_METRICS !== 'false'
